<template>
  <!-- ── Toast host ──────────────────────────────────────────────────────── -->
  <Teleport to="body">
    <div class="toast-host" aria-live="polite" aria-atomic="false">
      <TransitionGroup name="toast" tag="ul" class="toast-stack">
        <NotificationToast
          v-for="toast in store.activeToasts"
          :key="toast.id"
          :notification="toast"
          @dismiss="store.dismissToast"
        />
      </TransitionGroup>
    </div>
  </Teleport>

  <!-- ── Bell button + dropdown ─────────────────────────────────────────── -->
  <div class="nc-wrap">
    <PopoverRoot v-model:open="open">
      <PopoverTrigger as-child>
        <button
          class="btn btn-ghost btn-sm icon-btn nc-bell"
          :class="{ 'nc-bell--active': open }"
          :aria-label="`Notifications${store.bellUnreadCount ? ` (${store.bellUnreadCount} unread)` : ''}`"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <span v-if="store.bellUnreadCount" class="nc-badge">
            {{ store.bellUnreadCount > 9 ? '9+' : store.bellUnreadCount }}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent class="nc-panel" side="bottom" align="end" :side-offset="8">
          <div class="nc-panel__header">
            <span class="nc-panel__title label">Notifications</span>
            <div class="nc-header-actions">
              <div class="segmented">
                <button
                  type="button"
                  :aria-pressed="store.notificationScope === 'ALL'"
                  @click="() => saveScope('ALL')"
                >All games</button>
                <button
                  type="button"
                  :aria-pressed="store.notificationScope === 'CURRENT'"
                  @click="() => saveScope('CURRENT')"
                >This game</button>
              </div>
              <button
                v-if="store.bellUnreadCount"
                class="btn btn-ghost btn-sm"
                @click="store.markAllRead()"
              >Mark all read</button>
            </div>
          </div>

          <div class="nc-panel__body">
            <p v-if="store.bellItems.length === 0" class="nc-empty">
              No notifications yet.
            </p>
            <ul v-else class="nc-list">
              <li
                v-for="item in store.bellItems"
                :key="item.id"
                class="nc-item"
                :class="{ 'nc-item--unread': !item.read, 'nc-item--clickable': !!item.scenarioId }"
                @click="() => handleClick(item)"
              >
                <div class="nc-item__content">
                  <div class="nc-item__title-row">
                    <span class="badge" :class="severityBadge(item.type).cls">{{ severityBadge(item.type).label }}</span>
                    <span class="nc-item__title">{{ item.title }}</span>
                  </div>
                  <div v-if="item.body" class="nc-item__body">{{ item.body }}</div>
                  <div class="nc-item__time mono">{{ relativeTime(item.ts) }}</div>
                </div>
              </li>
            </ul>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  </div>

  <!-- ── Cross-scenario switch confirmation ──────────────────────────────── -->
  <ConfirmDialog
    v-model:open="confirmOpen"
    title="Switch games?"
    :description="confirmDescription"
    confirm-label="Switch"
    :dangerous="false"
    @confirm="confirmSwitch"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { PopoverRoot, PopoverTrigger, PopoverPortal, PopoverContent } from 'radix-vue'
import { useNotificationStore } from '@/stores/notifications'
import { useScenarioStore } from '@/stores/scenario'
import { usePlayerStore } from '@/stores/player'
import { resolveNotificationNavigation } from '@/utils/notificationNavigation'
import NotificationToast from './NotificationToast.vue'
import ConfirmDialog from './ConfirmDialog.vue'

const store        = useNotificationStore()
const scenarioStore = useScenarioStore()
const player       = usePlayerStore()
const route        = useRoute()
const router       = useRouter()
const open         = ref(false)

// Scenario the user is currently viewing — only player scenario routes
// (/scenarios/:id...). Admin routes (/admin/scenarios/:id) and neutral pages
// resolve to null, so navigating from them never prompts a switch dialog.
const currentScenarioId = computed(() =>
  route.params.id && route.path.startsWith('/scenarios/') ? route.params.id : null
)

// Pending cross-scenario navigation awaiting confirmation
const confirmOpen   = ref(false)
const pendingTarget = ref(null)
const pendingScenarioName = ref(null)
const confirmDescription = computed(() =>
  `This notification is from ${pendingScenarioName.value || 'another game'}. Switch games to view it?`
)

function handleClick(item) {
  store.markRead(item.id)
  const { target, needsConfirm } = resolveNotificationNavigation(item, currentScenarioId.value)
  if (!target) return
  if (needsConfirm) {
    pendingTarget.value = target
    pendingScenarioName.value =
      scenarioStore.scenarios?.find(s => s.id === item.scenarioId)?.name || null
    confirmOpen.value = true
    return
  }
  navigate(target)
}

function navigate(target) {
  open.value = false
  router.push(target)
}

function confirmSwitch() {
  const target = pendingTarget.value
  pendingTarget.value = null
  if (target) navigate(target)
}

// Mark all read when the panel is opened; persist to DB when inside a scenario
watch(open, val => {
  if (!val) return
  const scenarioId = scenarioStore.current?.id
  if (scenarioId) store.markAllReadRemote(scenarioId)
  else store.markAllRead()
})

async function saveScope(scope) {
  await player.saveProfile({ settings: { notificationScope: scope } })
}

// Severity badges deliberately collapse info/accent into "neutral" — accent
// (brass) is reserved for game-world actions, never a passive notification.
const SEVERITY_BADGES = {
  success: { cls: 'badge-success', label: 'Success' },
  warning: { cls: 'badge-warning', label: 'Warning' },
  danger:  { cls: 'badge-danger',  label: 'Alert' },
}
function severityBadge(type) {
  return SEVERITY_BADGES[type] || { cls: 'badge-neutral', label: 'Update' }
}

function relativeTime(ts) {
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000)
  if (diff < 60)    return 'just now'
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}
</script>

<style scoped>
/* ── Shared bell + wrap ───────────────────────────────────────────────── */
.nc-wrap {
  position: relative;
}

.nc-bell {
  position: relative;
  padding: 0.4em;
  line-height: 0;
}
.nc-bell svg { width: 16px; height: 16px; }
.nc-bell--active { color: var(--color-text); background: var(--color-surface-raised); }

.nc-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  background: var(--color-danger-subtle);
  color: var(--color-danger);
  font-size: 0.625rem;
  font-weight: 700;
  border-radius: var(--radius-pill);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  pointer-events: none;
}

/* ── Toast host (teleported to body) ─────────────────────────────────── */
.toast-host {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  pointer-events: none;
}

.toast-stack {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
  list-style: none;
  padding: 0;
  margin: 0;
  pointer-events: auto;
}

/* ── Toast transitions ────────────────────────────────────────────────── */
.toast-enter-active {
  animation: toast-in 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.toast-leave-active {
  animation: toast-out 0.18s ease-in forwards;
}
.toast-move {
  transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes toast-in {
  from { opacity: 0; transform: translateX(calc(100% + 1.5rem)); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes toast-out {
  from { opacity: 1; transform: translateX(0); max-height: 80px; }
  to   { opacity: 0; transform: translateX(calc(100% + 1.5rem)); max-height: 0; }
}
</style>

<!-- ── Panel styles — NOT scoped (teleported to body by Radix PopoverPortal) ── -->
<style>
.nc-panel {
  width: 360px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: 200;
  overflow: hidden;
}

.nc-panel[data-state="open"]   { animation: panel-in 0.16s cubic-bezier(0.16, 1, 0.3, 1); }
.nc-panel[data-state="closed"] { animation: panel-out 0.12s ease-in; }

.nc-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
}

.nc-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nc-panel__body {
  max-height: 420px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.nc-empty {
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.nc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nc-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border-subtle);
  cursor: default;
  transition: background 0.12s ease;
}
.nc-item:last-child { border-bottom: none; }
.nc-item--clickable { cursor: pointer; }
.nc-item:hover { background: var(--color-surface-raised); }
.nc-item--unread { background: var(--color-surface-raised); }

.nc-item__content { flex: 1; min-width: 0; }

.nc-item__title-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.nc-item__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
  line-height: 1.4;
}
.nc-item--unread .nc-item__title { font-weight: 600; }

.nc-item__body {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin-top: 0.1rem;
  line-height: 1.4;
}

.nc-item__time {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-top: 0.3rem;
}

@keyframes panel-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes panel-out {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(-6px) scale(0.98); }
}
</style>
